import { useEffect, useState, useMemo } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import PropTypes from 'prop-types';
import { Row, Col, Table, Spin, Tag, DatePicker, Select, InputNumber, Button, Modal } from 'antd';
import systemActions from '../../redux/systemInfos/actions';
import { DvIntlMessages } from "../../components/utility/dvGeneralUtilities";
import IntlMessages from '../../components/utility/intlMessages';
import moment from "moment";
import { debounce } from "lodash";
import memoize from "memoize-one";
import { DATE_FORMAT, DISPLAY_DATE_FORMAT, DISPLAY_DATE_TIME_FORMAT } from "../../helpers/utility";

const {
  getUserAuditLogs, resetUserAuditLogsData, getClinicNames, getUserNames
} = systemActions;

const { RangePicker } = DatePicker;

const DEFAULT_SORT_ORDER = 'descend';
const DEFAULT_RECORDS_PER_PAGE = 20;
const DEFAULT_PAGE_SIZE_OPTIONS = [20, 30, 40, 50];
const INIT_PAGE = 1;

const ACTIONS_OPTIONS = ['VIEW', 'UPDATE', 'CREATE', 'DESTROY', 'IMPORT'];

const URL_PAGE_MAP = {
  '/Dashboard/clinicDetails/:clinicId': 'clinicPage',
  '/Dashboard/userDetails/:userId': "userDetails",
  "/Dashboard/auditLogs": "auditLogs"
};

function AuditLogs(props) {
  const [currentPage, setCurrentPage] = useState(INIT_PAGE);
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER);
  const [recordsPerPage, setRecordsPerPage] = useState(DEFAULT_RECORDS_PER_PAGE);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [actionFilter, setActionFilter] = useState(null);
  const [userId, setUserId] = useState(null);
  const [clinicId, setClinicId] = useState(null);
  const [patientId, setPatientId] = useState(null);
  const [currentPath, setCurrentPath] = useState(null);
  const [modalIsVisible, setModalIsVisible] = useState(false);
  const [recordIdToShow, setRecordIdToShow] = useState(null)

  const users = useMemo(() => {
    const getFilteredUserNames = (clinicId) => {
      return clinicId ? props.userNames.filter((user) => user.clinicId === clinicId) : (props.userNames || [])
    }

    return getFilteredUserNames(clinicId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, props.userNames]);

  useEffect(() => {
    props.getClinicNames();
    props.getUserNames();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    props.resetUserAuditLogsData();

    let _clinicId, _user_id;
    const currentPath = URL_PAGE_MAP[props.match.path];
    setCurrentPath(currentPath);

    if (currentPath === 'clinicPage') {
      _clinicId = parseInt(props.match.params.clinicId);
      setClinicId(_clinicId);
    }
    else if (currentPath === 'userDetails') {
      _user_id = parseInt(props.match.params.userId);
      setUserId(_user_id);

      if (props.userDetails && props.userDetails?.id === _user_id && props.userDetails?.clinicId) {
        setClinicId(parseInt(props.userDetails?.clinicId));
      }
    }

    submitFilters(_clinicId, _user_id)

    return () => {
      props.resetUserAuditLogsData();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.match.path])

  function onChangeClinic(value) {
    setClinicId(value)
    setUserId(null);
    setPatientId(null);
  }

  function onChangeUser(value) {
    setUserId(value);
    setPatientId(null);
  }

  const onChangePatientId = debounce((value) => {
    setPatientId(value);
  }, 300);

  function onChangeDatepicker(values) {
    const tmp_startDate = values === null || values[0] === null ? null : moment(values[0]).format(DATE_FORMAT);
    const tmp_endDate = values === null || values[1] === null ? null : moment(values[1]).format(DATE_FORMAT);

    setStartDate(tmp_startDate);
    setEndDate(tmp_endDate);
  }

  function onChangeActionFilter(value) {
    setActionFilter(value? value : null);
  }

  function submitFilters(_clinicId = clinicId, _user_id = userId) {
    props.resetUserAuditLogsData();

    const filters = {
      "count": recordsPerPage,
      "page": INIT_PAGE,
      "action": actionFilter,
      "startDate": startDate,
      "endDate": endDate,
      "clinicId": _clinicId,
      "userId": _user_id,
      "patientId": patientId,
      "sortOrder": sortOrder === 'ascend' ? 'asc' : 'desc'
    };

    setCurrentPage(INIT_PAGE);

    props.getUserAuditLogs(filters);
  }

  const columns = [
    {
      title: DvIntlMessages("userAuditLogs.action"),
      dataIndex: 'action',
      key: 'action',
      render: (text, record) => {
        let color;
        let renderText = text;
        if (text === 'CREATE_OR_UPDATE') {
          renderText = record.newValues.id === undefined ? "CREATE" : "UPDATE";
        }
        switch (renderText) {
          case 'VIEW':
            color = "#108ee9";
            break;
          case 'UPDATE':
            color = "orange";
            break;
          case 'CREATE':
          case 'IMPORT':
            color = "#87d068";
            break;
          case 'DESTROY':
            color = "#f50";
            break;
          case 'CREATE_OR_UPDATE':
            color = 'linear-gradient(110deg, rgb(135, 208, 104) 50%, rgb(255, 165, 0) 50%)';
            break;
          default:
            color = "#87d068";
        }
        return {
          props: {
            style: { lineHeight: '20px', paddingTop: '0px', paddingBottom: '0px' }
          },
          children: (
            <span>
              <Tag key={renderText} style={{ background: color }}>
                {renderText}
              </Tag>
            </span>
          )
        };
      },
    },
    {
      title: DvIntlMessages("userAuditLogs.eventDate"),
      dataIndex: 'createdAt',
      defaultSortOrder: DEFAULT_SORT_ORDER,
      key: 'createdAt',
      sorter: (a, b) => {},
      render: (text) => <span>{moment(text).format(DISPLAY_DATE_TIME_FORMAT)}</span>,
    },
    {
      title: DvIntlMessages("homePage.backgrounLogs.userId"),
      dataIndex: 'userId',
      key: 'userId',
    },
    {
      title: DvIntlMessages("userAuditLogs.resourceType"),
      dataIndex: 'resourceType',
      key: 'resourceType',
      render: (text) => <span>{text}</span>,
    },
    {
      title: DvIntlMessages("clinicsPage.table.id"),
      dataIndex: 'resourceId',
      key: 'resourceId',
      render: (text) => <span>{text}</span>,
    },
    {
      title: DvIntlMessages("userAuditLogs.patientId"),
      dataIndex: 'patientId',
      key: 'patientId',
      render: (text, record) => {
        let valueToRender = text;
        if (!text) {
          valueToRender = '-';
        }
        return {
          props: {
            style: { lineHeight: '20px', paddingTop: '0px', paddingBottom: '0px' }
          },
          children: (
            <span>{valueToRender}</span>
          )
        };
      },
    },
    {
      title: DvIntlMessages("auditPage.data"),
      dataIndex: 'newValues',
      key: 'newValues',
      render: (text, record) => {
        return {
          props: {
            style: { lineHeight: '20px', paddingTop: '0px', paddingBottom: '0px' }
          },
          children: (
            <Button
              type="link"
              onClick={() => handleShowMoreButton(record)}
              style={{ lineHeight: '20px', paddingTop: '0px', paddingBottom: '0px' }}>
              <IntlMessages id="general.showData" /> ...
            </Button>
          )
        };
      },
    },
  ];

  function handleShowMoreButton(record) {
    setModalIsVisible(true);
    setRecordIdToShow(record.id);
  }

  function closeModal() {
    setModalIsVisible(false);
    setRecordIdToShow(null);
  }

  const getModalContent = memoize((selectedRecordId) => {
    let record = props.data.find((item) => item.id === selectedRecordId);
    if (record) {
      let recordData = record.newValues;
      return (
        <Row>
          <Col span={24}>
            <span>
              <pre style={{ maxHeight: '500px', overflow: 'auto' }}>
                {JSON.stringify(recordData, null, 2)}
              </pre>
            </span>
          </Col>
        </Row>
      );
    }
    return null;
  })

  function updateTableData(pagination, sorter) {
    const { current, pageSize } = pagination;
    let newPageNumber = current;

    if ((sorter.order !== sortOrder) || (pageSize !== recordsPerPage)) newPageNumber = INIT_PAGE;

    const filters = {
      "count": pageSize,
      "page": newPageNumber,
      "action": actionFilter,
      "startDate": startDate,
      "endDate": endDate,
      clinicId,
      userId,
      patientId,
      "sortOrder": sorter.order === 'ascend' ? 'asc' : 'desc'
    };

    props.getUserAuditLogs(filters);

    setCurrentPage(newPageNumber);
    setRecordsPerPage(pageSize);
    setSortOrder(sorter.order);
  }

  const tableColumns = columns.map(item => ({ ...item, ellipsis: true }));
  const paginationConfig = {
    showSizeChanger: true,
    pageSize: recordsPerPage,
    pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
    current: currentPage,
    total: props.numberOfLogs
  };
  const modalContent = getModalContent(recordIdToShow);

  return (
    <Spin spinning={props.loading > 0}>
      <Row gutter={[16, 16]} style={{ marginTop: "25px" }}>
        <Modal
          visible={modalIsVisible}
          width={'500px'}
          closable={false}
          footer={[
            <Button key="submit" type="primary" onClick={closeModal}>
              <IntlMessages id="general.close" />
            </Button>
          ]}
        >
          {modalContent}
        </Modal>

        <Col span={24}>
          <Row gutter={[16, 16]}>
            {(currentPath !== 'clinicPage' && currentPath !== 'userDetails') && <Col>
              <Select style={{ width: 190 }} onChange={onChangeClinic}
                placeholder={DvIntlMessages("general.clinic")} allowClear={true}>
                {props.clinicNames.map(item => {
                  return (
                    <Select.Option value={item.id} key={item.id}>
                      {item.title}
                    </Select.Option>
                  )
                })}
              </Select>
            </Col>}

            {currentPath !== 'userDetails' && <Col>
              <Select style={{ width: 190 }} onChange={onChangeUser} value={userId}
                placeholder={DvIntlMessages("general.user")} allowClear={true}>
                {users.map(item => {
                  return (
                    <Select.Option value={item.id} key={item.id}>
                      {item.fullName}
                    </Select.Option>
                  )
                })}
              </Select>
            </Col>}

            <Col>
              <InputNumber min={1} onChange={onChangePatientId} value={patientId}
                placeholder={DvIntlMessages('userAuditLogs.patientId')} />
            </Col>

            <Col>
              <RangePicker
                format={DISPLAY_DATE_FORMAT}
                allowEmpty={[true, true]}
                onChange={onChangeDatepicker}
              />
            </Col>

            <Col>
              <Select style={{ width: 190 }} onChange={onChangeActionFilter}
                placeholder={DvIntlMessages("userAuditLogs.action")} allowClear={true}>
                {ACTIONS_OPTIONS.map(value => {
                  return (
                    <Select.Option value={value} key={value}>
                      {value}
                    </Select.Option>
                  )
                })}
              </Select>
            </Col>

            <Col>
              <Button onClick={() => submitFilters()} type="primary">
                <IntlMessages id="general.submit" />
              </Button>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Table
            bordered={true}
            showHeader
            pagination={paginationConfig}
            columns={tableColumns}
            sortDirections={['descend', 'ascend', 'descend']}
            onChange={(pagination, filters, sorter) => updateTableData(pagination, sorter)}
            rowKey={(record) => record.id}
            dataSource={props.data}
            size='small'
            tableLayout='fixed'
          />
        </Col>

      </Row>
    </Spin>
  )
}

AuditLogs.propTypes = {
  data: PropTypes.array,
  numberOfLogs: PropTypes.number,
  loading: PropTypes.number.isRequired,
  clinicNames: PropTypes.array.isRequired,
  userNames: PropTypes.array.isRequired,
  userDetails: PropTypes.object.isRequired,

  getUserAuditLogs: PropTypes.func.isRequired,
  resetUserAuditLogsData: PropTypes.func.isRequired,
  getClinicNames: PropTypes.func.isRequired,
  getUserNames: PropTypes.func.isRequired
};

AuditLogs.defaultProps = {
  clinicNames: [],
  userNames: [],
  userDetails: {}
}

const mapActionsToProps = {
  getUserAuditLogs,
  resetUserAuditLogsData,
  getClinicNames,
  getUserNames
};

function mapStateToProps(state, props) {
  const userAuditLogsData = state.SystemInfos.get('userAuditLogs')
  return {
    data: userAuditLogsData?.actionLogs ?? [],
    numberOfLogs: userAuditLogsData?.numberOfLogs,
    loading: state.SystemInfos.get('loading'),
    clinicNames: state.SystemInfos.get('clinicNames'),
    userNames: state.SystemInfos.get('userNames'),
    userDetails: state.SystemInfos.get('userDetails')
  };
}

const wrappedAuditLogs = withRouter(AuditLogs);
const connectedAuditLogs = connect(mapStateToProps, mapActionsToProps)(wrappedAuditLogs);
export default connectedAuditLogs;